import { useCallback, useEffect, useState } from 'react';
import { fsopApi } from '@/services/api';
import type { RouteItem, RoutesResponse } from '@/types/routeMap';

interface UseRouteDataOptions {
  year?: number | 'all';
  mergeDirection?: boolean;
}

interface UseRouteDataResult {
  routes: RouteItem[];
  unknownAirports: string[];
  yearOptions: number[];
  loading: boolean;
  error: string | null;
  subscriptionRequired: boolean;
  refetch: () => Promise<void>;
}

export function useRouteData({ year = 'all', mergeDirection = false }: UseRouteDataOptions = {}): UseRouteDataResult {
  const [routes, setRoutes] = useState<RouteItem[]>([]);
  const [unknownAirports, setUnknownAirports] = useState<string[]>([]);
  const [yearOptions, setYearOptions] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSubscriptionRequired(false);
    try {
      const params: { year?: number; mergeDirection?: boolean } = {};
      if (year !== 'all') params.year = year;
      if (mergeDirection) params.mergeDirection = true;

      const res = await fsopApi.getRoutes(params);
      const payload: RoutesResponse = res.data?.data ?? res.data ?? {
        routes: [],
        unknownAirports: [],
        yearOptions: [],
      };
      setRoutes(payload.routes ?? []);
      setUnknownAirports(payload.unknownAirports ?? []);
      setYearOptions(payload.yearOptions ?? []);
    } catch (err: any) {
      if (err.response?.status === 402) {
        setSubscriptionRequired(true);
      } else {
        const message = err instanceof Error ? err.message : '加载航线数据失败';
        setError(message);
      }
      setRoutes([]);
      setUnknownAirports([]);
    } finally {
      setLoading(false);
    }
  }, [year, mergeDirection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { routes, unknownAirports, yearOptions, loading, error, subscriptionRequired, refetch: fetchData };
}
